import { Controller } from "@hotwired/stimulus"
import { turboFetch, handleTurboStream } from "./shared/turbo_fetch"

/**
 * Kanban Controller
 * @gem rapid_rails_ui
 *
 * Handles drag-and-drop card movement between columns with full keyboard
 * accessibility. Uses HTML5 native Drag and Drop API + ARIA live region
 * for screen reader announcements.
 *
 * Targets:
 * - column: Column containers
 * - cardList: Scrollable card list areas (drop targets)
 * - card: Draggable card elements
 * - liveRegion: Screen reader live region for announcements
 *
 * Values:
 * - draggable (Boolean): Enable drag-and-drop (default: true)
 * - moveUrl (String): URL for auto-PATCH on card move
 *
 * Events dispatched:
 * - kanban:move    { cardId, fromColumnId, toColumnId, position }
 * - kanban:grab    { cardId }
 * - kanban:cancel  { cardId }
 * - kanban:move-failed { cardId, error }
 */
export default class extends Controller {
  static targets = ["column", "cardList", "card", "liveRegion"]
  static values = {
    draggable: { type: Boolean, default: true },
    moveUrl: { type: String, default: "" }
  }

  connect() {
    this.draggedCard = null
    this.draggedFromColumn = null
    this.placeholder = null
    this.sourceGhost = null
    this.keyboardGrabbed = null
    this.originalPosition = null
    this._lastInsertRef = null
    this._lastCardList = null
    this._rafPending = false
    this._dragClone = null
  }

  disconnect() {
    this.#removePlaceholder()
    this.#removeSourceGhost()
    this.#removeDragClone()
    this.#cleanupDragState()
  }

  // ==========================================================================
  // DRAG AND DROP — Mouse/Touch
  // ==========================================================================

  dragstart(event) {
    if (!this.draggableValue) return

    const card = event.currentTarget
    this.draggedCard = card
    this.draggedFromColumn = card.closest("[data-column-id]")?.dataset.columnId

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.cardId)

    // Create tilted drag ghost — positioned at the card's exact location
    // so the browser can render the transform into the ghost bitmap.
    const rect = card.getBoundingClientRect()
    const clone = card.cloneNode(true)
    clone.style.cssText = [
      "position:fixed",
      `top:${rect.top}px`,
      `left:${rect.left}px`,
      `width:${rect.width}px`,
      `height:${rect.height}px`,
      "transform:rotate(-3deg) scale(1.04)",
      "box-shadow:0 16px 32px rgba(0,0,0,0.15),0 4px 8px rgba(0,0,0,0.1)",
      "pointer-events:none",
      "z-index:99999",
      "margin:0"
    ].join(";")
    document.body.appendChild(clone)
    this._dragClone = clone

    event.dataTransfer.setDragImage(clone, event.clientX - rect.left, event.clientY - rect.top)

    // Capture card height for placeholder sizing before collapsing
    this._draggedCardHeight = card.offsetHeight

    // Clean up clone + collapse source card after browser captures the ghost
    requestAnimationFrame(() => {
      this.#removeDragClone()

      // Collapse the source card so it "disappears" from its slot
      card.style.opacity = "0"
      card.style.height = "0"
      card.style.padding = "0"
      card.style.margin = "0"
      card.style.border = "none"
      card.style.overflow = "hidden"
      card.style.transition = "none"

      // Insert a dashed ghost outline where the card was
      this.#insertSourceGhost(card)
    })
  }

  dragend() {
    this.#removeDragClone()
    if (this.draggedCard) {
      this.#restoreCard(this.draggedCard)
    }
    this.#removeSourceGhost()
    this.draggedCard = null
    this.draggedFromColumn = null
    this._lastInsertRef = null
    this._lastCardList = null
    this._draggedCardHeight = null
    this.#removePlaceholder()
  }

  dragover(event) {
    if (!this.draggableValue || !this.draggedCard) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    const cardList = event.currentTarget
    const column = cardList.closest("[data-column-id]")

    // Check if column is droppable
    if (column?.dataset.droppable === "false") return

    // Check max cards
    const maxCards = column?.dataset.maxCards
    if (maxCards) {
      const currentCards = cardList.querySelectorAll("[data-card-id]").length
      const isFromThisColumn = column.dataset.columnId === this.draggedFromColumn
      if (currentCards >= parseInt(maxCards) && !isFromThisColumn) return
    }

    // Throttle DOM updates to one per animation frame
    const clientY = event.clientY
    if (!this._rafPending) {
      this._rafPending = true
      requestAnimationFrame(() => {
        this._rafPending = false
        if (this.draggedCard) {
          this.#insertPlaceholder(cardList, clientY)
        }
      })
    }
  }

  dragleave(event) {
    const cardList = event.currentTarget
    // Only remove if we actually left the card list (not entering a child)
    if (!cardList.contains(event.relatedTarget)) {
      this._lastInsertRef = null
      this._lastCardList = null
      this.#removePlaceholder()
    }
  }

  drop(event) {
    event.preventDefault()
    if (!this.draggedCard) return

    const cardList = event.currentTarget
    const column = cardList.closest("[data-column-id]")
    if (!column) return

    const toColumnId = column.dataset.columnId
    const cardId = this.draggedCard.dataset.cardId

    // Restore source card before placing
    this.#restoreCard(this.draggedCard)
    this.#removeSourceGhost()

    // Move card to placeholder position
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.insertBefore(this.draggedCard, this.placeholder)
    } else {
      cardList.appendChild(this.draggedCard)
    }
    this._lastInsertRef = null
    this._lastCardList = null
    this.#removePlaceholder()

    // Settle animation — card lands tilted and snaps straight
    this.#animateSettle(this.draggedCard)

    // Calculate position (0-based index among cards in the target list)
    const position = this.#cardPosition(cardList, cardId)

    // Dispatch event
    this.#dispatchMove(cardId, this.draggedFromColumn, toColumnId, position)

    // Auto-persist if moveUrl configured
    if (this.moveUrlValue) {
      this.#persistMove(cardId, toColumnId, position)
    }

    this.draggedCard = null
    this.draggedFromColumn = null
    this._draggedCardHeight = null
  }

  // ==========================================================================
  // KEYBOARD ACCESSIBILITY (WCAG 2.2 SC 2.5.7)
  // ==========================================================================

  cardKeydown(event) {
    if (!this.draggableValue) return

    const card = event.currentTarget

    switch (event.key) {
      case " ": // Space — grab/drop
        event.preventDefault()
        if (this.keyboardGrabbed === card) {
          this.#keyboardDrop(card)
        } else {
          this.#keyboardGrab(card)
        }
        break

      case "ArrowUp":
        event.preventDefault()
        if (this.keyboardGrabbed === card) {
          this.#keyboardMoveUp(card)
        }
        break

      case "ArrowDown":
        event.preventDefault()
        if (this.keyboardGrabbed === card) {
          this.#keyboardMoveDown(card)
        }
        break

      case "ArrowLeft":
        event.preventDefault()
        if (this.keyboardGrabbed === card) {
          this.#keyboardMoveToColumn(card, -1)
        }
        break

      case "ArrowRight":
        event.preventDefault()
        if (this.keyboardGrabbed === card) {
          this.#keyboardMoveToColumn(card, 1)
        }
        break

      case "Escape":
        if (this.keyboardGrabbed === card) {
          event.preventDefault()
          this.#keyboardCancel(card)
        }
        break
    }
  }

  // ==========================================================================
  // PRIVATE — Placeholder (card-sized dashed box)
  // ==========================================================================

  #insertPlaceholder(cardList, clientY) {
    const height = this._draggedCardHeight || 60

    if (!this.placeholder) {
      this.placeholder = document.createElement("div")
      this.placeholder.dataset.kanbanPlaceholder = "true"
      this.placeholder.style.cssText = [
        `height:${height}px`,
        "border:2px dashed rgba(96,165,250,0.6)",
        "border-radius:6px",
        "background:rgba(96,165,250,0.05)",
        "transition:height 150ms ease-out",
        "margin:0",
        "flex-shrink:0"
      ].join(";")
    }

    // Find the card closest to the cursor position
    const cards = cardList.children
    let insertBefore = null

    for (let i = 0; i < cards.length; i++) {
      const child = cards[i]
      if (child === this.placeholder || child === this.draggedCard) continue
      if (!child.dataset.cardId) continue

      const rect = child.getBoundingClientRect()
      if (clientY < rect.top + rect.height / 2) {
        insertBefore = child
        break
      }
    }

    // Bail if position hasn't changed
    if (insertBefore === this._lastInsertRef && cardList === this._lastCardList && this.placeholder.parentNode === cardList) return
    this._lastInsertRef = insertBefore
    this._lastCardList = cardList

    if (insertBefore) {
      cardList.insertBefore(this.placeholder, insertBefore)
    } else {
      cardList.appendChild(this.placeholder)
    }
  }

  #removePlaceholder() {
    if (this.placeholder && this.placeholder.parentNode) {
      this.placeholder.parentNode.removeChild(this.placeholder)
    }
    this.placeholder = null
  }

  // Dashed outline where the card was "lifted" from
  #insertSourceGhost(card) {
    this.#removeSourceGhost()
    const height = this._draggedCardHeight || 60
    this.sourceGhost = document.createElement("div")
    this.sourceGhost.style.cssText = [
      `height:${height}px`,
      "border:2px dashed rgba(148,163,184,0.35)",
      "border-radius:6px",
      "background:rgba(148,163,184,0.04)",
      "transition:height 250ms ease-out,opacity 250ms ease-out",
      "opacity:1",
      "flex-shrink:0"
    ].join(";")
    this.sourceGhost.dataset.kanbanSourceGhost = "true"

    card.parentNode.insertBefore(this.sourceGhost, card.nextSibling)

    // Shrink the ghost after a beat
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.sourceGhost) {
          this.sourceGhost.style.height = "4px"
          this.sourceGhost.style.opacity = "0.2"
        }
      })
    })
  }

  #removeSourceGhost() {
    if (this.sourceGhost && this.sourceGhost.parentNode) {
      this.sourceGhost.parentNode.removeChild(this.sourceGhost)
    }
    this.sourceGhost = null
  }

  #removeDragClone() {
    if (this._dragClone && this._dragClone.parentNode) {
      this._dragClone.remove()
    }
    this._dragClone = null
  }

  // Restore collapsed card to visible
  #restoreCard(card) {
    card.style.opacity = ""
    card.style.height = ""
    card.style.padding = ""
    card.style.margin = ""
    card.style.border = ""
    card.style.overflow = ""
    card.style.transition = ""
  }

  // ==========================================================================
  // PRIVATE — Drop Animation
  // ==========================================================================

  #animateSettle(card) {
    card.animate([
      {
        transform: "rotate(-3deg) scale(1.03)",
        boxShadow: "0 12px 28px rgba(0,0,0,0.14), 0 4px 8px rgba(0,0,0,0.08)",
        offset: 0
      },
      {
        transform: "rotate(0.8deg) scale(1.005)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        offset: 0.55
      },
      {
        transform: "rotate(0deg) scale(1)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
        offset: 1
      }
    ], {
      duration: 350,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)"
    })
  }

  // ==========================================================================
  // PRIVATE — Keyboard navigation
  // ==========================================================================

  #keyboardGrab(card) {
    this.keyboardGrabbed = card
    this.originalPosition = {
      parent: card.parentNode,
      nextSibling: card.nextElementSibling
    }

    card.classList.add("ring-2", "ring-blue-500", "shadow-lg")
    card.setAttribute("aria-grabbed", "true")

    card.animate([
      { transform: "scale(1)", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
      { transform: "scale(1.02)", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }
    ], { duration: 150, easing: "ease-out", fill: "forwards" })

    this.#announce(`Grabbed ${card.dataset.cardId}. Use arrow keys to move, Space to drop, Escape to cancel.`)

    this.dispatch("grab", { detail: { cardId: card.dataset.cardId } })
  }

  #keyboardDrop(card) {
    const cardList = card.closest(this.#targetSelector("cardList"))
    const column = card.closest("[data-column-id]")
    if (!column || !cardList) return

    const toColumnId = column.dataset.columnId
    const fromColumnId = this.originalPosition?.parent?.closest("[data-column-id]")?.dataset.columnId
    const cardId = card.dataset.cardId
    const position = this.#cardPosition(cardList, cardId)

    card.classList.remove("ring-2", "ring-blue-500", "shadow-lg")
    card.setAttribute("aria-grabbed", "false")

    this.#animateSettle(card)

    this.#announce(`Dropped ${cardId} in ${column.querySelector(this.#targetSelector("cardList"))?.getAttribute("aria-label") || toColumnId} at position ${position + 1}.`)

    this.#dispatchMove(cardId, fromColumnId, toColumnId, position)

    if (this.moveUrlValue) {
      this.#persistMove(cardId, toColumnId, position)
    }

    this.keyboardGrabbed = null
    this.originalPosition = null
  }

  #keyboardMoveUp(card) {
    const prev = card.previousElementSibling
    if (prev && !prev.dataset.kanbanPlaceholder) {
      card.parentNode.insertBefore(card, prev)
      card.focus()
      this.#announce(`Moved up.`)
    }
  }

  #keyboardMoveDown(card) {
    const next = card.nextElementSibling
    if (next) {
      card.parentNode.insertBefore(next, card)
      card.focus()
      this.#announce(`Moved down.`)
    }
  }

  #keyboardMoveToColumn(card, direction) {
    const columns = this.columnTargets
    const currentColumn = card.closest("[data-column-id]")
    const currentIndex = columns.indexOf(currentColumn)
    const targetIndex = currentIndex + direction

    if (targetIndex < 0 || targetIndex >= columns.length) return

    const targetColumn = columns[targetIndex]
    if (targetColumn.dataset.droppable === "false") return

    const maxCards = targetColumn.dataset.maxCards
    const targetCardList = targetColumn.querySelector(this.#targetSelector("cardList"))
    if (!targetCardList) return

    if (maxCards) {
      const currentCards = targetCardList.querySelectorAll("[data-card-id]").length
      if (currentCards >= parseInt(maxCards)) return
    }

    targetCardList.appendChild(card)
    card.focus()

    const columnTitle = targetCardList.getAttribute("aria-label") || targetColumn.dataset.columnId
    this.#announce(`Moved to ${columnTitle}.`)
  }

  #keyboardCancel(card) {
    if (this.originalPosition) {
      const { parent, nextSibling } = this.originalPosition
      if (nextSibling) {
        parent.insertBefore(card, nextSibling)
      } else {
        parent.appendChild(card)
      }
    }

    card.classList.remove("ring-2", "ring-blue-500", "shadow-lg")
    card.setAttribute("aria-grabbed", "false")
    card.focus()

    this.#animateSettle(card)

    this.#announce(`Move cancelled.`)
    this.dispatch("cancel", { detail: { cardId: card.dataset.cardId } })

    this.keyboardGrabbed = null
    this.originalPosition = null
  }

  // ==========================================================================
  // PRIVATE — Events & Persistence
  // ==========================================================================

  #dispatchMove(cardId, fromColumnId, toColumnId, position) {
    this.dispatch("move", {
      detail: { cardId, fromColumnId, toColumnId, position }
    })
  }

  #cardPosition(cardList, cardId) {
    const cards = [...cardList.querySelectorAll("[data-card-id]")]
    return cards.findIndex(c => c.dataset.cardId === cardId)
  }

  async #persistMove(cardId, columnId, position) {
    try {
      const response = await turboFetch(this.moveUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ card_id: cardId, column_id: columnId, position })
      })

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }

      await handleTurboStream(response)
    } catch (error) {
      this.dispatch("move-failed", {
        detail: { cardId, error: error.message }
      })
    }
  }

  // ==========================================================================
  // PRIVATE — Helpers
  // ==========================================================================

  #announce(message) {
    if (this.hasLiveRegionTarget) {
      this.liveRegionTarget.textContent = message
    }
  }

  // Build a target attribute selector that respects this.identifier so namespaced
  // hosts (e.g. "rui--kanban") still match.
  #targetSelector(name) {
    return `[data-${this.identifier}-target="${name}"]`
  }

  #cleanupDragState() {
    if (this.keyboardGrabbed) {
      this.keyboardGrabbed.classList.remove("ring-2", "ring-blue-500", "shadow-lg")
      this.keyboardGrabbed.setAttribute("aria-grabbed", "false")
      this.keyboardGrabbed = null
    }
    this.draggedCard = null
    this.originalPosition = null
  }
}
